/**
 * PIN Configuration
 * 
 * This file contains constants and utility functions for the PIN verification system.
 */

// Storage keys
export const PIN_KEY = 'loams_pin';
export const PIN_ATTEMPTS_KEY = 'loams_pin_attempts';
export const PIN_LOCKOUT_KEY = 'loams_pin_lockout';

// Default PIN for initial setup
export const DEFAULT_PIN = '8582';

// Lockout duration in minutes
export const LOCKOUT_DURATION = 10;

// Maximum number of PIN attempts before lockout
export const MAX_ATTEMPTS = 3;

// Roles that require PIN for secure access
export const PIN_REQUIRED_ROLES = [
    'super_admin',
    'admin',
    'accounts',
    'loams_only' // Make sure this is here
  ];

/**
 * Check if a user has a PIN setup
 * @returns {boolean} True if PIN is set up
 */
export const hasPinSetup = () => {
  return !!localStorage.getItem(PIN_KEY);
};

/**
 * Check if user role requires PIN verification
 * @param {string} role User role
 * @returns {boolean} True if PIN is required for the role
 */
export const isPinRequiredForRole = (role) => {
  if (!role) return false;
  return PIN_REQUIRED_ROLES.includes(role.toLowerCase());
};

/**
 * Check if user needs PIN verification based on stored user data
 * @returns {boolean} True if current user requires PIN verification
 */
export const checkPinRequiredAccess = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role?.toLowerCase() || '';
  return isPinRequiredForRole(role);
};

/**
 * Calculate lockout expiration time
 * @returns {number} Timestamp when lockout expires
 */
export const calculateLockoutTime = () => {
  return Date.now() + (LOCKOUT_DURATION * 60 * 1000);
};

/**
 * Format lockout time for display
 * @param {number} lockoutTime Timestamp of lockout expiration
 * @returns {string} Formatted time string (e.g., "9:59")
 */
export const formatLockoutTime = (lockoutTime) => {
  if (!lockoutTime) return '';
  
  const remainingTime = Math.max(0, lockoutTime - Date.now());
  const minutes = Math.floor(remainingTime / 60000);
  const seconds = Math.floor((remainingTime % 60000) / 1000);
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Reset PIN attempts counter
 */
export const resetAttempts = () => {
  localStorage.setItem(PIN_ATTEMPTS_KEY, MAX_ATTEMPTS.toString());
};

/**
 * Check if account is currently locked out
 * @returns {boolean} True if account is locked out
 */
export const isAccountLocked = () => {
  const lockoutUntil = localStorage.getItem(PIN_LOCKOUT_KEY);
  if (!lockoutUntil) return false;
  
  const lockoutTime = parseInt(lockoutUntil, 10);
  return lockoutTime > Date.now();
};

/**
 * Get remaining PIN attempts
 * @returns {number} Number of remaining attempts
 */
export const getRemainingAttempts = () => {
  const attempts = localStorage.getItem(PIN_ATTEMPTS_KEY);
  return attempts ? parseInt(attempts, 10) : MAX_ATTEMPTS;
};


export default {
  PIN_KEY,
  PIN_ATTEMPTS_KEY,
  PIN_LOCKOUT_KEY,
  DEFAULT_PIN,
  LOCKOUT_DURATION,
  MAX_ATTEMPTS,
  PIN_REQUIRED_ROLES,
  hasPinSetup,
  isPinRequiredForRole,
  checkPinRequiredAccess,
  calculateLockoutTime,
  formatLockoutTime,
  resetAttempts,
  isAccountLocked,
  getRemainingAttempts
};